import { Link, useParams } from '@tanstack/react-router'
import type { ComponentType } from 'react'

import type { Language } from '@/bindings/Language'
import { NotFoundPage } from '@/app/not-found'
import { Button } from '@/components/button'
import { Card, Page } from '@/components/layout'
import { BranchOperationEn } from '@/features/guide/content/branch-operation.en'
import { BranchOperationVi } from '@/features/guide/content/branch-operation.vi'
import { CreateRestaurantEn } from '@/features/guide/content/create-restaurant.en'
import { CreateRestaurantVi } from '@/features/guide/content/create-restaurant.vi'
import { SetupBranchEn } from '@/features/guide/content/setup-branch.en'
import { SetupBranchVi } from '@/features/guide/content/setup-branch.vi'
import { SetupRestaurantEn } from '@/features/guide/content/setup-restaurant.en'
import { SetupRestaurantVi } from '@/features/guide/content/setup-restaurant.vi'
import { GUIDES, type GuideSlug, isGuideSlug } from '@/features/guide/guides'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerShell } from '@/features/ordering/customer-chrome'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { printPage } from '@/lib/live'
import { formatDate } from '@/lib/time'

const CONTENT: Record<GuideSlug, Record<Language, ComponentType>> = {
  'create-restaurant': { en: CreateRestaurantEn, vi: CreateRestaurantVi },
  'setup-restaurant': { en: SetupRestaurantEn, vi: SetupRestaurantVi },
  'setup-branch': { en: SetupBranchEn, vi: SetupBranchVi },
  'branch-operation': { en: BranchOperationEn, vi: BranchOperationVi },
}

/**
 * `/app/guide/$guide` — the public user guide (2026-10-02): one tab per lifecycle
 * step, each its own route so support can link straight to it. Printing
 * drops the chrome and tabs, so the browser's Save as PDF is the download.
 */
export function GuidePage() {
  const t = useT()
  const language = useLanguage()
  const auth = useCustomerAuth()
  const { guide: slug } = useParams({ from: '/app/guide/$guide' })
  const step = GUIDES.findIndex((g) => g.slug === slug)
  const guide = GUIDES[step]
  if (!guide || !isGuideSlug(slug)) {
    return (
      <CustomerShell color={undefined} footer="copyright">
        <CustomerHeader detail={t('guide.title')} auth={auth} />
        <NotFoundPage home="/" />
      </CustomerShell>
    )
  }
  const Content = CONTENT[slug][language]

  return (
    <CustomerShell color={undefined} footer="copyright">
      <CustomerHeader detail={t('guide.title')} auth={auth} />
      <Page width="md" className="flex flex-1 flex-col gap-4">
        <p className="text-muted text-sm print:hidden">{t('guide.intro')}</p>
        <nav aria-label={t('guide.title')} className="-mx-4 overflow-x-auto px-4 print:hidden sm:mx-0 sm:px-0">
          <ul className="flex gap-1">
            {GUIDES.map((g, i) => (
              <li key={g.slug} className="shrink-0">
                <Link
                  to="/app/guide/$guide"
                  params={{ guide: g.slug }}
                  aria-current={g.slug === slug ? 'page' : undefined}
                  className={cx(
                    'flex min-h-tap items-center rounded-lg px-3 text-sm font-medium',
                    g.slug === slug ? 'bg-accent text-accent-fg' : 'text-accent-ink hover:bg-accent-soft',
                  )}
                >
                  {i + 1}. {t(g.label)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <Card className="flex flex-col gap-3 p-6 print:border-0 print:p-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold">
                {step + 1}. {t(guide.label)}
              </h1>
              <p className="text-muted text-sm">{t('guide.updated', { date: formatDate(guide.updated, language) })}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={printPage} className="print:hidden">
              {t('guide.print')}
            </Button>
          </div>
          <article className="flex flex-col gap-3">
            <Content />
          </article>
        </Card>
      </Page>
    </CustomerShell>
  )
}
